import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	FormattedText,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	stringToLocalDate,
	StyleProps,
	toGrammaticalNumber,
	TruncatedText,
} from "@hex-insights/core";
import { Icon } from "../../../../Components";
import { StudentAttendanceNotesListQuery } from "../../../../Utilities";
import { StudentFormat, UserFormat } from "../../../Format";
import { AttendanceStatusBadge } from "../../AttendanceStatus";
import { StudentMultiIcon } from "../../Student";
import { UserIcon } from "../../User";
import styles from "./styles.module.css";

export type AttendanceNotesListModalControllerProps = Pick<AttendanceNotesListModalProps, "attendanceNotes"> & {
	slim?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function AttendanceNotesListModalController({
	attendanceNotes,
	slim = false,
	className,
	style,
}: AttendanceNotesListModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant="link" size="small" onClick={openModal} className={className} style={style}>
				<Conditional>
					<If condition={slim}>
						<Icon.WarningDiamond size={20} className="block" />
					</If>
					<Else>
						<Row justify="spaced-start" align="center">
							<Icon.WarningDiamond size={20} />
							<span>{toGrammaticalNumber("Note", attendanceNotes.length, true)}</span>
						</Row>
					</Else>
				</Conditional>
			</Button>

			<Modal.If condition={isModalOpen}>
				<AttendanceNotesListModal attendanceNotes={attendanceNotes} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

export type AttendanceNotesListModalProps = {
	attendanceNotes: StudentAttendanceNotesListQuery["studentNoteConnection"]["edges"][0]["node"][];
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function AttendanceNotesListModal({
	attendanceNotes,
	ifRef,
	onClose,
	className,
	style,
}: AttendanceNotesListModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={className} style={{ ...style, height: "30rem" }}>
			<Modal.Header>
				<Heading.H2 noMargin>Attendance Notes</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					{attendanceNotes.map((attendanceNote) => (
						<Column key={attendanceNote.id} justify="spaced-start" className={styles["attendance-note"]}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
								<StudentMultiIcon students={attendanceNote.primaryStudents} size={32} />

								<TruncatedText className={styles["attendance-note__students__names"]}>
									{englishCommaJoinStrings(attendanceNote.primaryStudents.map((e) => StudentFormat.name(e)))}
								</TruncatedText>
							</Row>

							<FormattedText className={styles["attendance-note__body"]}>{attendanceNote.body}</FormattedText>

							<Row justify="space-between" align="center">
								{!!attendanceNote.studentAttendanceNote && (
									<Row justify="spaced-start" align="center">
										{!!attendanceNote.studentAttendanceNote.attendanceStatus && (
											<AttendanceStatusBadge
												attendanceStatus={attendanceNote.studentAttendanceNote.attendanceStatus}
												style={{ fontSize: "0.875rem" }}
											/>
										)}

										<div className={styles["attendance-note__dates"]}>
											{attendanceNote.studentAttendanceNote.startDate === attendanceNote.studentAttendanceNote.endDate
												? stringToLocalDate(attendanceNote.studentAttendanceNote.startDate, "day").toLocaleString(
														undefined,
														{ month: "short", day: "numeric", weekday: "short" },
												  )
												: stringToLocalDate(attendanceNote.studentAttendanceNote.startDate, "day").toLocaleString(
														undefined,
														{ month: "short", day: "numeric", weekday: "short" },
												  ) +
												  " - " +
												  stringToLocalDate(attendanceNote.studentAttendanceNote.endDate, "day").toLocaleString(
														undefined,
														{
															month: "short",
															day: "numeric",
															weekday: "short",
														},
												  )}
										</div>
									</Row>
								)}

								{!!attendanceNote.author && (
									<Row
										justify="spaced-start"
										align="center"
										horizontalSpacing="0.5rem"
										className={styles["attendance-note__author"]}
									>
										<UserIcon user={attendanceNote.author} size={20} />
										<strong>{UserFormat.name(attendanceNote.author)}</strong>
									</Row>
								)}
							</Row>
						</Column>
					))}
				</Column>
			</Modal.Body>
		</Modal>
	);
}
